import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './CompactAnalysisDialog.css';
import { multilinearOLS, kMeans, analyzeEinflussgroessenZeitbestimmend, buildDesignMatrixEA, simpleOLSWithBand } from '../utils/analysisStats';
import EgRegressionChart from './EgRegressionChart';
import AnalysisPieChart from './AnalysisPieChart';
import AnalysisBarChart from './AnalysisBarChart';
import TimePill from './TimePill';
import ZeitstrahlWaBlock from './ZeitstrahlWaBlock';

// Deterministische Farbe aus String (HSL) - fuer Kuerzel/Gruppen ohne hinterlegte Farbe
function deterministicColor(input, darkMode = false) {
  const s = String(input || '');
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) % 360;
  }
  const sat = darkMode ? 55 : 60;
  const lig = darkMode ? 58 : 48;
  return `hsl(${h}, ${sat}%, ${lig}%)`;
}

// localStorage-Helfer mit Whitelist (Default zurueck bei unbekanntem Wert)
function readLSChoice(key, allowed, fallback) {
  try {
    const v = localStorage.getItem(key);
    if (v && allowed.includes(v)) return v;
  } catch { /* ignore */ }
  return fallback;
}
function writeLS(key, value) {
  try { localStorage.setItem(key, value); } catch { /* ignore */ }
}

// WICHTIG: TOP-LEVEL-Komponente. Innere Komponenten werden bei jedem Render des
// Eltern-Containers neu erzeugt -> React unmounted/remountet sie, wodurch Klicks
// (z.B. auf "Oeffnen ->" oder TimePill in der Kachel) bei Hover-/State-Re-Renders
// verloren gehen. Daher MUSS diese Komponente ausserhalb von CompactAnalysisDialog
// definiert sein. Vgl. AnalysisDashboard.TileHeader.
function CompactDashboardTile({ title, hint, openMode, openLabel = 'Öffnen', extra, onOpen, children }) {
  return (
    <div className="compact-dashboard-tile">
      <div className="compact-dashboard-tile-header">
        <div className="compact-dashboard-tile-title" title={hint || title}>{title}</div>
        <div className="compact-dashboard-tile-header-right">
          {extra}
          {openMode && onOpen && (
            <button
              type="button"
              className="compact-dashboard-tile-open"
              onClick={(e) => { e.stopPropagation(); onOpen(openMode); }}
              title={`${openLabel}: ${title}`}
            >{openLabel} →</button>
          )}
        </div>
      </div>
      <div className="compact-dashboard-tile-body">{children}</div>
    </div>
  );
}

// Fallback-Farben für Zeitarten (wenn timeTypes ohne .color oder für "Keine Zeitart")
const DEFAULT_ZEITART_COLORS = {
  th: '#3b82f6', tn: '#10b981', thb: '#6366f1', tnb: '#14b8a6',
  thu: '#8b5cf6', tnu: '#06b6d4', twu: '#f59e0b', tvs: '#60a5fa',
  tvp: '#ec4899', 'stö': '#ef4444', n__: '#22c55e', f__: '#9ca3af',
  tr: '#a855f7', tmz: '#fbbf24', tvk: '#93c5fd', tvv: '#93c5fd',
  x__: '#d1d5db', 'Keine Zeitart': '#94a3b8'
};

// Fallback-Beschreibungen für Zeitarten (wenn timeTypes ohne .description)
const DEFAULT_ZEITART_DESCRIPTIONS = {
  th: 'Hauptzeit', tn: 'Nebenzeit', thb: 'beeinflussbare Hauptzeit', tnb: 'beeinflussbare Nebenzeit',
  thu: 'unbeeinflussbare Hauptzeit', tnu: 'unbeeinflussbare Nebenzeit', twu: 'ablaufbedingte Wartezeit',
  tvs: 'sachliche Verteilzeit', tvp: 'persönliche Verteilzeit', 'stö': 'Störzeit', n__: 'nicht abzugeltende Zeit',
  f__: 'fallweise abzugeltende Zeit', tr: 'Rüstzeit', tmz: 'zusätzliche Tätigkeiten',
  tvk: 'Verteilzeit Schicht konstant', tvv: 'Verteilzeit Schicht variabel', x__: 'nicht zu verwendende Zeit',
  'Keine Zeitart': ''
};

const CompactAnalysisDialog = ({
  analysis,
  formatTime,
  formatSollzeit = null,
  decimalPlacesSollzeiten = 4,
  onClose,
  darkMode = false,
  pivotDrillDown = null,
  setPivotDrillDown = null,
  timeTypes = [], // Array von { name, description } für Langtexte
  ablaufDbData = [], // Array von { kuerzel, beschreibung, fullText } für Kürzel-Beschreibungen
  measurementPhotos = {}, // { measurementNumber: [ { id, fileData, fileName } ] } für Hover-Fotos
  onPhotoPreview = null // (fileData) => void, z.B. für Lightbox
}) => {
  const { t } = useTranslation();
  // Fallback für formatSollzeit, falls nicht übergeben
  const formatSollzeitSafe = formatSollzeit || ((val, places) => ((val || 0) / 100).toFixed(places));
  const [viewMode, setViewMode] = useState(() =>
    readLSChoice('compactAnalysisViewMode', ['dashboard', 'timeType', 'kuerzel', 'zeitstrahl', 'pivot', 'statistik'], 'timeType')
  );
  const [statistikGroupBy, setStatistikGroupBy] = useState('kuerzel'); // 'kuerzel' | 'kuerzelE' | 'kuerzelEA'
  const [statistikDisplay, setStatistikDisplay] = useState(() =>
    readLSChoice('compactStatistikDisplay', ['table', 'bar', 'kreis'], 'table')
  );
  const [bilanzZeitartenChartType, setBilanzZeitartenChartType] = useState(() =>
    readLSChoice('compactBilanzZeitartenChartType', ['tabelle', 'balken', 'kreis'], 'tabelle')
  );
  const [bilanzKuerzelChartType, setBilanzKuerzelChartType] = useState(() =>
    readLSChoice('compactBilanzKuerzelChartType', ['tabelle', 'balken', 'kreis'], 'tabelle')
  );
  const [bilanzZeitartenTimeMode, setBilanzZeitartenTimeMode] = useState(() =>
    readLSChoice('compactBilanzZeitartenTimeMode', ['ist', 'soll', 'vergleich'], 'vergleich')
  );
  const [bilanzKuerzelTimeMode, setBilanzKuerzelTimeMode] = useState(() =>
    readLSChoice('compactBilanzKuerzelTimeMode', ['ist', 'soll', 'vergleich'], 'vergleich')
  );
  const [compactDrillOrigin, setCompactDrillOrigin] = useState('tab'); // 'tab' | 'dashboard'
  const [eaDrillDown, setEaDrillDown] = useState(null); // { key, kuerzel, egList, selectedEG } | null
  const [zeitstrahlExpanded, setZeitstrahlExpanded] = useState(() => new Set()); // WA-Nr-Keys, die aufgeklappt sind (Start: alle zugeklappt)
  const [zeitstrahlHover, setZeitstrahlHover] = useState(null); // { m, rect } für Hover-Popover
  const zeitstrahlHoverTimeoutRef = useRef(null);

  useEffect(() => { writeLS('compactAnalysisViewMode', viewMode); }, [viewMode]);
  useEffect(() => { writeLS('compactStatistikDisplay', statistikDisplay); }, [statistikDisplay]);
  useEffect(() => { writeLS('compactBilanzZeitartenChartType', bilanzZeitartenChartType); }, [bilanzZeitartenChartType]);
  useEffect(() => { writeLS('compactBilanzKuerzelChartType', bilanzKuerzelChartType); }, [bilanzKuerzelChartType]);
  useEffect(() => { writeLS('compactBilanzZeitartenTimeMode', bilanzZeitartenTimeMode); }, [bilanzZeitartenTimeMode]);
  useEffect(() => { writeLS('compactBilanzKuerzelTimeMode', bilanzKuerzelTimeMode); }, [bilanzKuerzelTimeMode]);

  useEffect(() => {
    if (viewMode !== 'zeitstrahl') setZeitstrahlHover(null);
  }, [viewMode]);

  // Drill-Down aus dem Dashboard heraus (z.B. Klick auf eine Pivot-Zelle in der
  // Pivot-Kachel) erfaesst die Quelle automatisch - so dass "Zurueck" zum Dashboard
  // fuehrt, nicht zum jeweiligen Tab. Die expliziten Setter in den Bilanz-Klick-
  // Handlern bleiben aus Robustheit erhalten.
  useEffect(() => {
    if (pivotDrillDown && viewMode === 'dashboard') {
      setCompactDrillOrigin('dashboard');
    }
  }, [pivotDrillDown, viewMode]);

  // Hilfsfunktion: Farbe einer Zeitart aus timeTypes + Fallback
  const colorForTimeType = (name) => {
    const tt = timeTypes.find(t => t.name === name);
    return (tt && tt.color) || DEFAULT_ZEITART_COLORS[name] || '#9ca3af';
  };
  const colorForKuerzel = (name) => deterministicColor(name, darkMode);

  // Zentraler Wechsel des Reiters: Origin auf 'tab' zuruecksetzen
  const switchTab = (mode) => {
    setCompactDrillOrigin('tab');
    setViewMode(mode);
  };

  // Drill-Down schliessen: bei Dashboard-Origin zurueck zum Dashboard, sonst Tab bleibt
  const closeDrillDown = () => {
    setPivotDrillDown && setPivotDrillDown(null);
    setEaDrillDown(null);
    if (compactDrillOrigin === 'dashboard') {
      setViewMode('dashboard');
    }
    setCompactDrillOrigin('tab');
  };

  if (!analysis) return null;

  // Render-Funktion für Pivot-Ansicht
  // Hilfsfunktion: Berechne Durchschnitt, wobei 0-Werte ausgeschlossen werden
  const calculateAvgGrade = (grades) => {
    const nonZeroGrades = grades.filter(g => g !== null && g !== undefined && g !== 0);
    if (nonZeroGrades.length > 0) {
      return (nonZeroGrades.reduce((a, b) => a + b, 0) / nonZeroGrades.length).toFixed(1);
    }
    return 0;
  };

  // Hilfsfunktion: Formatiere Leistungsgrad, wobei 0% nicht angezeigt wird
  const formatGrade = (avgGrade) => {
    if (!avgGrade || avgGrade === '0' || avgGrade === '0.0' || parseFloat(avgGrade) === 0) {
      return '';
    }
    return `Ø ${avgGrade}%`;
  };

  // Toolbar fuer Bilanz-Reiter: Tabelle/Balken/Kreis + (bei Diagrammen) TimePill IST/SOLL/Vergleich
  const renderBilanzToolbar = ({ chartType, setChartType, timeMode, setTimeMode }) => (
    <div className="compact-chart-toolbar">
      <span className="compact-statistik-toolbar-label">Anzeige:</span>
      <button
        className={`compact-view-mode-btn ${chartType === 'tabelle' ? 'active' : ''}`}
        onClick={() => setChartType('tabelle')}
        title="Tabellenansicht"
      >Tabelle</button>
      <button
        className={`compact-view-mode-btn ${chartType === 'balken' ? 'active' : ''}`}
        onClick={() => setChartType('balken')}
        title="Balkendiagramm"
      >Balken</button>
      <button
        className={`compact-view-mode-btn ${chartType === 'kreis' ? 'active' : ''}`}
        onClick={() => setChartType('kreis')}
        title="Kreis-/Donut-Diagramm"
      >Kreis</button>
      {chartType !== 'tabelle' && (
        <>
          <span className="compact-statistik-toolbar-label" style={{ marginLeft: 8 }}>Zeit:</span>
          <TimePill
            value={timeMode}
            onChange={setTimeMode}
            darkMode={darkMode}
            options={[
              { id: 'ist', label: 'IST', title: 'Nur IST-Zeiten' },
              { id: 'soll', label: 'SOLL', title: 'Nur SOLL-Zeiten' },
              { id: 'vergleich', label: 'Vergleich', title: 'IST und SOLL gemeinsam' },
            ]}
            ariaLabel="Zeit-Modus"
          />
        </>
      )}
    </div>
  );

  // Bilanz-Daten in rows fuer AnalysisBarChart / AnalysisPieChart konvertieren
  const buildBilanzRows = (groupData, keys, colorFn) => keys.map(k => {
    const d = groupData[k] || {};
    return {
      key: k,
      label: k,
      ist: d.totalTime || 0,
      soll: d.totalVorgabeTime || 0,
      color: colorFn(k),
      count: d.count || 0,
    };
  });

  // Balken-Block fuer Bilanz: bei 'ist'/'soll' nur eine Spalte, bei 'vergleich' beide gepaart
  const renderBilanzBars = (rows, timeMode, onRowClick) => {
    if (timeMode === 'ist') {
      return (
        <AnalysisBarChart
          rows={rows}
          formatIst={formatTime}
          formatSoll={(n) => `${formatSollzeitSafe(n, decimalPlacesSollzeiten)} min`}
          showSoll={false}
          sortBy="ist"
          darkMode={darkMode}
          onRowClick={onRowClick}
        />
      );
    }
    if (timeMode === 'soll') {
      const swapped = rows.map(r => ({ ...r, ist: r.soll, soll: 0 }));
      return (
        <AnalysisBarChart
          rows={swapped}
          formatIst={(n) => `${formatSollzeitSafe(n, decimalPlacesSollzeiten)} min`}
          formatSoll={(n) => `${formatSollzeitSafe(n, decimalPlacesSollzeiten)} min`}
          showSoll={false}
          sortBy="ist"
          darkMode={darkMode}
          onRowClick={onRowClick}
        />
      );
    }
    return (
      <AnalysisBarChart
        rows={rows}
        formatIst={formatTime}
        formatSoll={(n) => `${formatSollzeitSafe(n, decimalPlacesSollzeiten)} min`}
        showSoll={true}
        sortBy="ist"
        darkMode={darkMode}
        onRowClick={onRowClick}
      />
    );
  };

  // Kreis-Block fuer Bilanz: bei 'ist'/'soll' ein Donut, bei 'vergleich' zwei Donuts STACKED (Portrait)
  const renderBilanzPies = (rows, timeMode, onSliceClick, pieSize = 200) => {
    const istSlices = rows.map(r => ({ key: r.key, label: r.label, value: r.ist || 0, color: r.color, count: r.count }));
    const sollSlices = rows.map(r => ({ key: r.key, label: r.label, value: r.soll || 0, color: r.color, count: r.count }));
    if (timeMode === 'ist') {
      return (
        <div className="compact-pie-wrap">
          <AnalysisPieChart
            slices={istSlices}
            formatValue={formatTime}
            darkMode={darkMode}
            size={pieSize}
            onSliceClick={onSliceClick}
            ariaLabel="IST-Donut"
          />
        </div>
      );
    }
    if (timeMode === 'soll') {
      return (
        <div className="compact-pie-wrap">
          <AnalysisPieChart
            slices={sollSlices}
            formatValue={(n) => `${formatSollzeitSafe(n, decimalPlacesSollzeiten)} min`}
            darkMode={darkMode}
            size={pieSize}
            onSliceClick={onSliceClick}
            ariaLabel="SOLL-Donut"
          />
        </div>
      );
    }
    // vergleich: stacked
    return (
      <div className="compact-pie-stacked">
        <div className="compact-pie-stacked-item">
          <div className="compact-pie-stacked-caption">IST</div>
          <AnalysisPieChart
            slices={istSlices}
            formatValue={formatTime}
            darkMode={darkMode}
            size={pieSize}
            onSliceClick={onSliceClick}
            ariaLabel="IST-Donut"
          />
        </div>
        <div className="compact-pie-stacked-item">
          <div className="compact-pie-stacked-caption">SOLL</div>
          <AnalysisPieChart
            slices={sollSlices}
            formatValue={(n) => `${formatSollzeitSafe(n, decimalPlacesSollzeiten)} min`}
            darkMode={darkMode}
            size={pieSize}
            onSliceClick={onSliceClick}
            ariaLabel="SOLL-Donut"
          />
        </div>
      </div>
    );
  };

  const renderPivotView = () => {
    const activities = Object.keys(analysis.pivot).sort();
    // Filtere "x__" Zeitarten aus der Pivot-Tabelle
    const timeTypes = [...new Set(Object.values(analysis.pivot).flatMap(activity => Object.keys(activity)))].filter(tt => tt !== 'x__').sort();
    
    const rowTotals = {};
    const colTotals = {};
    let grandTotal = { count: 0, totalTime: 0, totalVorgabeTime: 0, grades: [] };
    
    activities.forEach(activity => {
      rowTotals[activity] = { count: 0, totalTime: 0, totalVorgabeTime: 0, grades: [] };
      Object.keys(analysis.pivot[activity]).filter(tt => tt !== 'x__').forEach(timeType => {
        const cell = analysis.pivot[activity][timeType];
        rowTotals[activity].count += cell.count;
        rowTotals[activity].totalTime += cell.totalTime;
        rowTotals[activity].totalVorgabeTime += (cell.totalVorgabeTime || 0);
        rowTotals[activity].grades.push(...cell.grades);
        grandTotal.count += cell.count;
        grandTotal.totalTime += cell.totalTime;
        grandTotal.totalVorgabeTime += (cell.totalVorgabeTime || 0);
        grandTotal.grades.push(...cell.grades);
      });
      rowTotals[activity].avgGrade = calculateAvgGrade(rowTotals[activity].grades);
    });
    
    timeTypes.forEach(timeType => {
      colTotals[timeType] = { count: 0, totalTime: 0, totalVorgabeTime: 0, grades: [] };
      activities.forEach(activity => {
        if (analysis.pivot[activity][timeType]) {
          const cell = analysis.pivot[activity][timeType];
          colTotals[timeType].count += cell.count;
          colTotals[timeType].totalTime += cell.totalTime;
          colTotals[timeType].totalVorgabeTime += (cell.totalVorgabeTime || 0);
          colTotals[timeType].grades.push(...cell.grades);
        }
      });
      colTotals[timeType].avgGrade = calculateAvgGrade(colTotals[timeType].grades);
    });
    
    return (
      <div className="compact-pivot-table-wrapper">
        <table className="compact-pivot-table">
          <thead>
            <tr>
              <th className="compact-pivot-corner">{t('compact_analysis.taetigkeit_zeitart')}</th>
              {timeTypes.map(timeType => (
                <th key={timeType} className="compact-pivot-col-header">
                  <div className="compact-pivot-header-content">
                    <span className="compact-pivot-header-title">{timeType}</span>
                  </div>
                </th>
              ))}
              <th className="compact-pivot-row-total-header">{t('compact_analysis.gesamt')}</th>
            </tr>
          </thead>
          <tbody>
            {activities.map(activity => (
              <tr key={activity} className="compact-pivot-row">
                <td className="compact-pivot-row-header">
                  <div className="compact-pivot-row-header-content">
                    <span className="compact-pivot-row-title">{activity}</span>
                  </div>
                </td>
                {timeTypes.map(timeType => {
                  const cell = analysis.pivot[activity][timeType];
                  return (
                    <td 
                      key={timeType} 
                      className={`compact-pivot-cell ${cell && cell.count > 0 ? 'has-data' : 'no-data'}`}
                      onClick={() => {
                        if (cell && cell.count > 0 && setPivotDrillDown) {
                          setPivotDrillDown({ activity, timeType, measurements: cell.measurements });
                        }
                      }}
                      title={cell && cell.count > 0 ? (cell.count === 1 ? t('compact_analysis.click_details_one', { count: cell.count }) : t('compact_analysis.click_details_other', { count: cell.count })) : t('compact_analysis.keine_daten')}
                    >
                      {cell && cell.count > 0 ? (
                        <div className="compact-pivot-cell-content">
                          <div className="compact-pivot-count">{cell.count}</div>
                          <div className="compact-pivot-time">{formatTime(cell.totalTime)}</div>
                          <div className="compact-pivot-time" style={{ fontSize: '0.85em', opacity: 0.8 }}>S: {formatSollzeitSafe(cell.totalVorgabeTime || 0, decimalPlacesSollzeiten)} min</div>
                          {formatGrade(cell.avgGrade) && <div className="compact-pivot-grade">{formatGrade(cell.avgGrade)}</div>}
                        </div>
                      ) : (
                        <div className="compact-pivot-cell-empty">—</div>
                      )}
                    </td>
                  );
                })}
                <td className="compact-pivot-row-total">
                  <div className="compact-pivot-cell-content">
                    <div className="compact-pivot-count">{rowTotals[activity].count}</div>
                    <div className="compact-pivot-time">{formatTime(rowTotals[activity].totalTime)}</div>
                    <div className="compact-pivot-time" style={{ fontSize: '0.85em', opacity: 0.8 }}>S: {formatSollzeitSafe(rowTotals[activity].totalVorgabeTime || 0, decimalPlacesSollzeiten)} min</div>
                  </div>
                </td>
              </tr>
            ))}
            <tr className="compact-pivot-total-row">
              <td className="compact-pivot-col-total-header">{t('compact_analysis.gesamt')}</td>
              {timeTypes.map(timeType => (
                <td key={timeType} className="compact-pivot-col-total">
                  <div className="compact-pivot-cell-content">
                    <div className="compact-pivot-count">{colTotals[timeType].count}</div>
                    <div className="compact-pivot-time">{formatTime(colTotals[timeType].totalTime)}</div>
                    <div className="compact-pivot-time" style={{ fontSize: '0.85em', opacity: 0.8 }}>S: {formatSollzeitSafe(colTotals[timeType].totalVorgabeTime || 0, decimalPlacesSollzeiten)} min</div>
                  </div>
                </td>
              ))}
              <td className="compact-pivot-grand-total">
                <div className="compact-pivot-cell-content">
                  <div className="compact-pivot-count">{grandTotal.count}</div>
                  <div className="compact-pivot-time">{formatTime(grandTotal.totalTime)}</div>
                  <div className="compact-pivot-time" style={{ fontSize: '0.85em', opacity: 0.8 }}>S: {formatSollzeitSafe(grandTotal.totalVorgabeTime || 0, decimalPlacesSollzeiten)} min</div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  // Render-Funktion für Kürzel-Ansicht
  const renderKuerzelView = () => {
    // Sortiere so, dass "Kein Kürzel" am Ende steht
    const kuerzels = Object.keys(analysis.byKuerzel || {}).sort((a, b) => {
      if (a === 'Kein Kürzel') return 1;
      if (b === 'Kein Kürzel') return -1;
      return a.localeCompare(b);
    });
    let grandTotal = { count: 0, totalTime: 0, totalVorgabeTime: 0, grades: [] };

    kuerzels.forEach(kuerzel => {
      const data = analysis.byKuerzel[kuerzel];
      grandTotal.count += data.count;
      grandTotal.totalTime += data.totalTime;
      grandTotal.totalVorgabeTime += (data.totalVorgabeTime || 0);
      grandTotal.grades.push(...data.grades);
    });

    // Hole Beschreibung für Kürzel
    const getKuerzelDescription = (kuerzelName) => {
      if (kuerzelName === 'Kein Kürzel') return '';
      const kuerzelObj = ablaufDbData.find(k => k.kuerzel === kuerzelName);
      return kuerzelObj ? (kuerzelObj.beschreibung || kuerzelObj.fullText || '') : '';
    };

    const bilanzRowsK = buildBilanzRows(analysis.byKuerzel || {}, kuerzels, colorForKuerzel);
    const onKuerzelDrill = (key) => {
      const data = (analysis.byKuerzel || {})[key];
      if (data?.measurements?.length && setPivotDrillDown) {
        setPivotDrillDown({ kuerzel: key, measurements: data.measurements });
      }
    };

    return (
      <>
        {renderBilanzToolbar({
          chartType: bilanzKuerzelChartType,
          setChartType: setBilanzKuerzelChartType,
          timeMode: bilanzKuerzelTimeMode,
          setTimeMode: setBilanzKuerzelTimeMode,
        })}

        {bilanzKuerzelChartType === 'balken' && (
          <div className="compact-bilanz-chart-wrap">
            {renderBilanzBars(bilanzRowsK, bilanzKuerzelTimeMode, onKuerzelDrill)}
          </div>
        )}
        {bilanzKuerzelChartType === 'kreis' && (
          <div className="compact-bilanz-chart-wrap">
            {renderBilanzPies(bilanzRowsK, bilanzKuerzelTimeMode, onKuerzelDrill)}
          </div>
        )}

        {bilanzKuerzelChartType === 'tabelle' && (
        <div className="compact-simple-table-wrapper">
          <table className="compact-simple-table">
            <thead>
              <tr>
                <th>Kürzel</th>
                <th>{t('compact_analysis.taetigkeit')}</th>
                <th>{t('compact_analysis.anzahl')}</th>
                <th>{t('compact_analysis.gesamtzeit_ist')}</th>
                <th>{t('compact_analysis.vorgabezeit_soll')}</th>
                <th>{t('compact_analysis.avg_leistungsgrad')}</th>
              </tr>
            </thead>
            <tbody>
              {kuerzels.map(kuerzel => {
                const data = analysis.byKuerzel[kuerzel];
                const description = getKuerzelDescription(kuerzel);
                return (
                  <tr 
                    key={kuerzel}
                    className="compact-simple-row"
                    onClick={() => {
                      if (data.measurements && data.measurements.length > 0 && setPivotDrillDown) {
                        setPivotDrillDown({ kuerzel, measurements: data.measurements });
                      }
                    }}
                    title={data.measurements && data.measurements.length > 0 ? (data.count === 1 ? t('compact_analysis.click_details_one', { count: data.count }) : t('compact_analysis.click_details_other', { count: data.count })) : ''}
                  >
                    <td className="compact-simple-label">{kuerzel}</td>
                    <td className="compact-simple-label" style={{ fontSize: '0.9em', opacity: 0.8 }}>{description || '-'}</td>
                    <td className="compact-simple-value">{data.count}</td>
                    <td className="compact-simple-value">{formatTime(data.totalTime)}</td>
                    <td className="compact-simple-value">{formatSollzeitSafe(data.totalVorgabeTime || 0, decimalPlacesSollzeiten)} min</td>
                    <td className="compact-simple-value">{formatGrade(data.avgGrade)}</td>
                  </tr>
                );
              })}
              <tr className="compact-simple-total-row">
                <td className="compact-simple-total-label">{t('compact_analysis.gesamt')}</td>
                <td className="compact-simple-total-label"></td>
                <td className="compact-simple-total-value">{grandTotal.count}</td>
                <td className="compact-simple-total-value">{formatTime(grandTotal.totalTime)}</td>
                <td className="compact-simple-total-value">{formatSollzeitSafe(grandTotal.totalVorgabeTime || 0, decimalPlacesSollzeiten)} min</td>
                <td className="compact-simple-total-value"></td>
              </tr>
            </tbody>
          </table>
        </div>
        )}
        
        {/* Kürzel mit x__ Zeitarten am Ende */}
        {Object.keys(analysis.excludedKuerzels || {}).length > 0 && (() => {
        const excludedKuerzels = Object.keys(analysis.excludedKuerzels || {}).sort((a, b) => {
          if (a === 'Kein Kürzel') return 1;
          if (b === 'Kein Kürzel') return -1;
          return a.localeCompare(b);
        });
        let excludedGrandTotal = { count: 0, totalTime: 0, totalVorgabeTime: 0, grades: [] };
        excludedKuerzels.forEach(kuerzel => {
          const data = analysis.excludedKuerzels[kuerzel];
          excludedGrandTotal.count += data.count;
          excludedGrandTotal.totalTime += data.totalTime;
          excludedGrandTotal.totalVorgabeTime += (data.totalVorgabeTime || 0);
          excludedGrandTotal.grades.push(...data.grades);
        });
        return (
          <div className="compact-simple-table-wrapper" style={{ marginTop: '20px' }}>
            <h4 style={{ marginBottom: '10px', fontSize: '14px', fontWeight: 'bold', color: darkMode ? '#f9fafb' : '#111827' }}>{t('compact_analysis.kuerzel_x_zeit_header')}</h4>
            <table className="compact-simple-table">
              <thead>
                <tr>
                  <th>Kürzel</th>
                  <th>{t('compact_analysis.taetigkeit')}</th>
                  <th>{t('compact_analysis.anzahl')}</th>
                  <th>{t('compact_analysis.gesamtzeit_ist')}</th>
                </tr>
              </thead>
              <tbody>
                {excludedKuerzels.map(kuerzel => {
                  const data = analysis.excludedKuerzels[kuerzel];
                  const description = getKuerzelDescription(kuerzel);
                  return (
                    <tr 
                      key={kuerzel}
                      className="compact-simple-row"
                      onClick={() => {
                        if (data.measurements && data.measurements.length > 0 && setPivotDrillDown) {
                          setPivotDrillDown({ kuerzel, measurements: data.measurements });
                        }
                      }}
                      title={data.measurements && data.measurements.length > 0 ? (data.count === 1 ? t('compact_analysis.click_details_one', { count: data.count }) : t('compact_analysis.click_details_other', { count: data.count })) : ''}
                    >
                      <td className="compact-simple-label">{kuerzel}</td>
                      <td className="compact-simple-label" style={{ fontSize: '0.9em', opacity: 0.8 }}>{description || '-'}</td>
                      <td className="compact-simple-value">{data.count}</td>
                      <td className="compact-simple-value">{formatTime(data.totalTime)}</td>
                    </tr>
                  );
                })}
                <tr className="compact-simple-total-row">
                  <td className="compact-simple-total-label">{t('compact_analysis.gesamt')}</td>
                  <td className="compact-simple-total-label"></td>
                  <td className="compact-simple-total-value">{excludedGrandTotal.count}</td>
                  <td className="compact-simple-total-value">{formatTime(excludedGrandTotal.totalTime)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        );
      })()}
      </>
    );
  };

  // Render-Funktion für Zeitstrahl: Drill-down pro WA-Nr (zugeklappt), beim Aufklappen jede Tätigkeit als Zeile mit positioniertem Balken
  const renderZeitstrahlView = () => {
    const flatMeasurements = Object.values(analysis.byTimeType || {}).flatMap(d => d.measurements || []);
    const byWaNr = {};
    flatMeasurements.forEach(m => {
      const key = m.resumeNumber ?? '—';
      if (!byWaNr[key]) byWaNr[key] = [];
      byWaNr[key].push(m);
    });
    const orderKeys = Object.keys(byWaNr).sort((a, b) => {
      if (a === '—') return 1;
      if (b === '—') return -1;
      const na = Number(a);
      const nb = Number(b);
      if (!isNaN(na) && !isNaN(nb)) return na - nb;
      return String(a).localeCompare(String(b));
    });
    const fallbackColor = '#9ca3af';
    const getColor = (m) => {
      const key = (m.timeType == null || m.timeType === '') ? 'Keine Zeitart' : m.timeType;
      const tt = timeTypes.find(t => t.name === key);
      return (tt && tt.color) || DEFAULT_ZEITART_COLORS[key] || fallbackColor;
    };
    const toT = (m) => {
      const v = m?.timestamp ? new Date(m.timestamp).getTime() : 0;
      return (typeof v === 'number' && !isNaN(v)) ? v : 0;
    };
    const toggleWa = (waKey) => {
      setZeitstrahlExpanded(prev => {
        const n = new Set(prev);
        if (n.has(waKey)) n.delete(waKey); else n.add(waKey);
        return n;
      });
    };

    return (
      <div className="compact-zeitstrahl" style={{ marginTop: 8 }}>
        {orderKeys.map(waKey => {
          const list = [...(byWaNr[waKey] || [])].sort((a, b) => toT(a) - toT(b));
          const totalIstWa = list.reduce((s, m) => s + (m.individualTime || 0), 0);
          const label = waKey === '—' ? 'Ohne WA-Nr' : `WA-Nr ${waKey}`;
          const expanded = zeitstrahlExpanded.has(waKey);
          const tätigkeitenText = list.map(m => m.kuerzel || m.activity || ('#' + (m.number ?? ''))).filter(Boolean).join(' · ') || '—';
          return (
            <div key={waKey} className="compact-zeitstrahl-wa-block">
              <div
                className="compact-zeitstrahl-wa-header"
                onClick={() => toggleWa(waKey)}
                title={expanded ? 'Zuklappen' : 'Aufklappen'}
              >
                <span className="compact-zeitstrahl-chevron">{expanded ? '▼' : '▶'}</span>
                <div className="compact-zeitstrahl-label" title={`${label} · ${formatTime(totalIstWa)}`}>
                  {label} · {formatTime(totalIstWa)}
                </div>
                <div className="compact-zeitstrahl-track">
                  {list.map((m, idx) => {
                    const pct = totalIstWa > 0 ? ((m.individualTime || 0) / totalIstWa) * 100 : 0;
                    const color = getColor(m);
                    return (
                      <div
                        key={`${waKey}-${m.number}-${idx}`}
                        className="compact-zeitstrahl-segment"
                        style={{ width: pct + '%', backgroundColor: color, minWidth: pct > 0 ? 2 : 0 }}
                        onMouseEnter={(e) => { clearTimeout(zeitstrahlHoverTimeoutRef.current); setZeitstrahlHover({ m, rect: e.currentTarget.getBoundingClientRect() }); }}
                        onMouseLeave={() => { zeitstrahlHoverTimeoutRef.current = setTimeout(() => setZeitstrahlHover(null), 180); }}
                      />
                    );
                  })}
                </div>
              </div>
              {!expanded && (
                <div className="compact-zeitstrahl-sublabel compact-zeitstrahl-sublabel-indent" title={tätigkeitenText}>
                  {tätigkeitenText}
                </div>
              )}
              {expanded && (
                <div className="compact-zeitstrahl-drilldown">
                  {list.map((m, idx) => {
                    const pctStart = totalIstWa > 0
                      ? (list.slice(0, idx).reduce((s, x) => s + (x.individualTime || 0), 0) / totalIstWa) * 100
                      : 0;
                    const pct = totalIstWa > 0 ? ((m.individualTime || 0) / totalIstWa) * 100 : 0;
                    const color = getColor(m);
                    const rowLabel = [m.kuerzel, m.activity, '#' + (m.number ?? '')].find(Boolean) || '—';
                    return (
                      <div
                        key={`${waKey}-${m.number}-${idx}`}
                        className="compact-zeitstrahl-activity-row"
                        onMouseEnter={(e) => { clearTimeout(zeitstrahlHoverTimeoutRef.current); setZeitstrahlHover({ m, rect: e.currentTarget.getBoundingClientRect() }); }}
                        onMouseLeave={() => { zeitstrahlHoverTimeoutRef.current = setTimeout(() => setZeitstrahlHover(null), 180); }}
                      >
                        <span className="compact-zeitstrahl-activity-spacer" />
                        <div className="compact-zeitstrahl-activity-label">
                          #{m.number} {rowLabel} · {formatTime(m.individualTime || 0)}
                        </div>
                        <div className="compact-zeitstrahl-track compact-zeitstrahl-track-positioned">
                          <div
                            className="compact-zeitstrahl-segment"
                            style={{
                              position: 'absolute',
                              left: pctStart + '%',
                              width: pct + '%',
                              height: '100%',
                              backgroundColor: color,
                              borderRadius: 2
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        {zeitstrahlHover && (() => {
          const { m, rect } = zeitstrahlHover;
          const descZeitart = timeTypes.find(t => t.name === m.timeType)?.description || DEFAULT_ZEITART_DESCRIPTIONS[m.timeType] || '';
          const kuerzelMatch = (ablaufDbData && Array.isArray(ablaufDbData)) ? ablaufDbData.find(k => k.kuerzel === m.kuerzel) : null;
          const descKuerzel = kuerzelMatch ? (kuerzelMatch.beschreibung || kuerzelMatch.fullText || '') : '';
          const eaArr = m.einflussgroessen && typeof m.einflussgroessen === 'object' && !Array.isArray(m.einflussgroessen)
            ? Object.entries(m.einflussgroessen).map(([k, v]) => `${k}: ${v != null ? String(v) : ''}`)
            : [];
          const photos = (measurementPhotos[m.number] || []);
          let left = rect.left;
          let top = rect.bottom + 6;
          const w = 320;
          if (left + w > window.innerWidth) left = window.innerWidth - w - 8;
          if (left < 8) left = 8;
          if (top + 340 > window.innerHeight - 8 && rect.top > 360) { top = rect.top - 344; }
          return (
            <div
              className={`zeitstrahl-popover ${darkMode ? 'zeitstrahl-popover-dark' : ''}`}
              style={{ position: 'fixed', left, top, maxWidth: w, maxHeight: 340, zIndex: 10000 }}
              onMouseEnter={() => clearTimeout(zeitstrahlHoverTimeoutRef.current)}
              onMouseLeave={() => setZeitstrahlHover(null)}
            >
              <div className="zeitstrahl-popover-header">
                #{m.number} {m.kuerzel || m.activity || '—'} · {formatTime(m.individualTime || 0)} · {m.timeType || 'Keine Zeitart'}
              </div>
              {descZeitart && <div className="zeitstrahl-popover-line"><strong>{t('compact_analysis.drilldown_zeitart')}</strong> {descZeitart}</div>}
              {descKuerzel && <div className="zeitstrahl-popover-line"><strong>{t('compact_analysis.drilldown_taetigkeit')}</strong> {descKuerzel}</div>}
              {m.note && <div className="zeitstrahl-popover-line"><strong>Notiz:</strong> {m.note}</div>}
              {eaArr.length > 0 && <div className="zeitstrahl-popover-line"><strong>Einflussgrößen:</strong> {eaArr.join(', ')}</div>}
              {photos.length > 0 && (
                <div className="zeitstrahl-popover-photos">
                  {photos.map((p) => (
                    <img
                      key={p.id}
                      src={p.fileData}
                      alt={p.fileName}
                      title={p.fileName}
                      onClick={() => { if (onPhotoPreview) onPhotoPreview(p.fileData); setZeitstrahlHover(null); }}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })()}
        {orderKeys.length === 0 && (
          <div className="compact-zeitstrahl-empty">{t('compact_analysis.zeitstrahl_empty')}</div>
        )}
      </div>
    );
  };

  // Render-Funktion für Zeitarten-Ansicht
  const renderTimeTypeView = () => {
    // Sortiere so, dass "Keine Zeitart" am Ende steht
    const timeTypeKeys = Object.keys(analysis.byTimeType || {}).sort((a, b) => {
      if (a === 'Keine Zeitart') return 1;
      if (b === 'Keine Zeitart') return -1;
      return a.localeCompare(b);
    });
    let grandTotal = { count: 0, totalTime: 0, totalVorgabeTime: 0, grades: [] };

    timeTypeKeys.forEach(timeType => {
      const data = analysis.byTimeType[timeType];
      grandTotal.count += data.count;
      grandTotal.totalTime += data.totalTime;
      grandTotal.totalVorgabeTime += (data.totalVorgabeTime || 0);
      grandTotal.grades.push(...data.grades);
    });

    // Hole Beschreibung für Zeitarten
    const getTimeTypeDescription = (timeTypeName) => {
      const timeTypeObj = timeTypes.find(tt => tt.name === timeTypeName);
      return timeTypeObj ? timeTypeObj.description : '';
    };

    // Sammle x__ Zeitarten
    const excludedTimeTypes = Object.keys(analysis.excludedTimeTypes || {}).sort();
    let excludedGrandTotal = { count: 0, totalTime: 0, totalVorgabeTime: 0, grades: [] };
    excludedTimeTypes.forEach(timeType => {
      const data = analysis.excludedTimeTypes[timeType];
      excludedGrandTotal.count += data.count;
      excludedGrandTotal.totalTime += data.totalTime;
      excludedGrandTotal.totalVorgabeTime += (data.totalVorgabeTime || 0);
      excludedGrandTotal.grades.push(...data.grades);
    });
    const bilanzRows = buildBilanzRows(analysis.byTimeType || {}, timeTypeKeys, colorForTimeType);
    const onTimeTypeDrill = (key) => {
      const data = (analysis.byTimeType || {})[key];
      if (data?.measurements?.length && setPivotDrillDown) {
        setPivotDrillDown({ timeType: key, measurements: data.measurements });
      }
    };

    return (
      <>
        {renderBilanzToolbar({
          chartType: bilanzZeitartenChartType,
          setChartType: setBilanzZeitartenChartType,
          timeMode: bilanzZeitartenTimeMode,
          setTimeMode: setBilanzZeitartenTimeMode,
        })}

        {bilanzZeitartenChartType === 'balken' && (
          <div className="compact-bilanz-chart-wrap">
            {renderBilanzBars(bilanzRows, bilanzZeitartenTimeMode, onTimeTypeDrill)}
          </div>
        )}
        {bilanzZeitartenChartType === 'kreis' && (
          <div className="compact-bilanz-chart-wrap">
            {renderBilanzPies(bilanzRows, bilanzZeitartenTimeMode, onTimeTypeDrill)}
          </div>
        )}

        {bilanzZeitartenChartType === 'tabelle' && (
        <div className="compact-simple-table-wrapper">
          <table className="compact-simple-table">
            <thead>
              <tr>
                <th>{t('compact_analysis.zeitart')}</th>
                <th>{t('compact_analysis.beschreibung')}</th>
                <th>{t('compact_analysis.anzahl')}</th>
                <th>{t('compact_analysis.gesamtzeit_ist')}</th>
                <th>{t('compact_analysis.vorgabezeit_soll')}</th>
                <th>{t('compact_analysis.avg_leistungsgrad')}</th>
              </tr>
            </thead>
            <tbody>
              {timeTypeKeys.map(timeType => {
                const data = analysis.byTimeType[timeType];
                const description = getTimeTypeDescription(timeType);
                return (
                  <tr 
                    key={timeType}
                    className="compact-simple-row"
                    onClick={() => {
                      if (data.measurements && data.measurements.length > 0 && setPivotDrillDown) {
                        setPivotDrillDown({ timeType, measurements: data.measurements });
                      }
                    }}
                    title={data.measurements && data.measurements.length > 0 ? (data.count === 1 ? t('compact_analysis.click_details_one', { count: data.count }) : t('compact_analysis.click_details_other', { count: data.count })) : ''}
                  >
                    <td className="compact-simple-label">{timeType}</td>
                    <td className="compact-simple-label" style={{ fontSize: '0.9em', opacity: 0.8 }}>{description || '-'}</td>
                    <td className="compact-simple-value">{data.count}</td>
                    <td className="compact-simple-value">{formatTime(data.totalTime)}</td>
                    <td className="compact-simple-value">{formatSollzeitSafe(data.totalVorgabeTime || 0, decimalPlacesSollzeiten)} min</td>
                    <td className="compact-simple-value">{formatGrade(data.avgGrade)}</td>
                  </tr>
                );
              })}
              <tr className="compact-simple-total-row">
                <td className="compact-simple-total-label">{t('compact_analysis.gesamt')}</td>
                <td className="compact-simple-total-label"></td>
                <td className="compact-simple-total-value">{grandTotal.count}</td>
                <td className="compact-simple-total-value">{formatTime(grandTotal.totalTime)}</td>
                <td className="compact-simple-total-value">{formatSollzeitSafe(grandTotal.totalVorgabeTime || 0, decimalPlacesSollzeiten)} min</td>
                <td className="compact-simple-total-value"></td>
              </tr>
            </tbody>
          </table>
        </div>
        )}
        
        {/* x__ Zeitarten am Ende */}
        {excludedTimeTypes.length > 0 && (
          <div className="compact-simple-table-wrapper" style={{ marginTop: '20px' }}>
            <h4 style={{ marginBottom: '10px', fontSize: '14px', fontWeight: 'bold', color: darkMode ? '#f9fafb' : '#111827' }}>{t('compact_analysis.excluded_time_header')}</h4>
            <table className="compact-simple-table">
              <thead>
                <tr>
                  <th>{t('compact_analysis.zeitart')}</th>
                  <th>{t('compact_analysis.beschreibung')}</th>
                  <th>{t('compact_analysis.anzahl')}</th>
                  <th>{t('compact_analysis.gesamtzeit_ist')}</th>
                </tr>
              </thead>
              <tbody>
                {excludedTimeTypes.map(timeType => {
                  const data = analysis.excludedTimeTypes[timeType];
                  const description = getTimeTypeDescription(timeType);
                  return (
                    <tr 
                      key={timeType}
                      className="compact-simple-row"
                      onClick={() => {
                        if (data.measurements && data.measurements.length > 0 && setPivotDrillDown) {
                          setPivotDrillDown({ timeType, measurements: data.measurements });
                        }
                      }}
                      title={data.measurements && data.measurements.length > 0 ? (data.count === 1 ? t('compact_analysis.click_details_one', { count: data.count }) : t('compact_analysis.click_details_other', { count: data.count })) : ''}
                    >
                      <td className="compact-simple-label">{timeType}</td>
                      <td className="compact-simple-label" style={{ fontSize: '0.9em', opacity: 0.8 }}>{description || '-'}</td>
                      <td className="compact-simple-value">{data.count}</td>
                      <td className="compact-simple-value">{formatTime(data.totalTime)}</td>
                    </tr>
                  );
                })}
                <tr className="compact-simple-total-row">
                  <td className="compact-simple-total-label">{t('compact_analysis.gesamt')}</td>
                  <td className="compact-simple-total-label"></td>
                  <td className="compact-simple-total-value">{excludedGrandTotal.count}</td>
                  <td className="compact-simple-total-value">{formatTime(excludedGrandTotal.totalTime)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </>
    );
  };

  // Render-Funktion für Statistik-Ansicht (Min, Max, Mittelwert, Std, Varianz, Spannweite, Var.-Koeff.; Kürzel / Kürzel+EA; Tabelle / Balken; Regression, Clustering)
  const renderStatistikView = () => {
    const groupData = statistikGroupBy === 'kuerzel' ? (analysis.byKuerzel || {}) : (statistikGroupBy === 'kuerzelE' ? (analysis.byKuerzelE || {}) : (analysis.byKuerzelEA || {}));
    const groupKeys = Object.keys(groupData).sort((a, b) => {
      if (a === 'Kein Kürzel' || a.startsWith('Kein Kürzel')) return 1;
      if (b === 'Kein Kürzel' || b.startsWith('Kein Kürzel')) return -1;
      return a.localeCompare(b);
    });
    const maxMean = groupKeys.length ? Math.max(...groupKeys.map(k => groupData[k].mean || 0)) : 1;

    return (
      <>
        <div className="compact-statistik-toolbar">
          <span className="compact-statistik-toolbar-label">Gruppierung:</span>
          <button
            className={`compact-view-mode-btn ${statistikGroupBy === 'kuerzel' ? 'active' : ''}`}
            onClick={() => setStatistikGroupBy('kuerzel')}
          >Kürzel</button>
          <button
            className={`compact-view-mode-btn ${statistikGroupBy === 'kuerzelE' ? 'active' : ''}`}
            onClick={() => setStatistikGroupBy('kuerzelE')}
          >Kürzel + E</button>
          <button
            className={`compact-view-mode-btn ${statistikGroupBy === 'kuerzelEA' ? 'active' : ''}`}
            onClick={() => setStatistikGroupBy('kuerzelEA')}
          >Kürzel + EA</button>
          <span className="compact-statistik-toolbar-label">Darstellung:</span>
          <button
            className={`compact-view-mode-btn ${statistikDisplay === 'table' ? 'active' : ''}`}
            onClick={() => setStatistikDisplay('table')}
          >Tabelle</button>
          <button
            className={`compact-view-mode-btn ${statistikDisplay === 'bar' ? 'active' : ''}`}
            onClick={() => setStatistikDisplay('bar')}
          >Balken</button>
          <button
            className={`compact-view-mode-btn ${statistikDisplay === 'kreis' ? 'active' : ''}`}
            onClick={() => setStatistikDisplay('kreis')}
          >Kreis</button>
        </div>

        {statistikDisplay === 'table' && (
          <div className="compact-simple-table-wrapper" style={{ marginTop: 8 }}>
            <table className="compact-simple-table compact-statistik-table">
              <thead>
                <tr>
                  <th>{statistikGroupBy === 'kuerzel' ? 'Kürzel' : (statistikGroupBy === 'kuerzelE' ? 'Kürzel + E' : 'Kürzel + EA')}</th>
                  <th style={{ textAlign: 'center' }}>n</th>
                  <th style={{ textAlign: 'center' }}>Min</th>
                  <th style={{ textAlign: 'center' }}>Max</th>
                  <th style={{ textAlign: 'center' }}>Mittelwert</th>
                  <th style={{ textAlign: 'center' }}>Std</th>
                  <th style={{ textAlign: 'center' }}>Varianz</th>
                  <th style={{ textAlign: 'center' }}>Spannweite</th>
                  <th style={{ textAlign: 'center' }}>Var.-Koeff. %</th>
                </tr>
              </thead>
              <tbody>
                {groupKeys.map(k => {
                  const d = groupData[k];
                  const egKeysKuerzel = statistikGroupBy === 'kuerzel' ? [...new Set((d.measurements || []).flatMap(m => (m.einflussgroessen && typeof m.einflussgroessen === 'object') ? Object.keys(m.einflussgroessen) : []))] : [];
                  const isClickable = statistikGroupBy === 'kuerzel';
                  return (
                    <tr
                      key={k}
                      onClick={isClickable ? () => setEaDrillDown({ key: k, kuerzel: k, egList: egKeysKuerzel, selectedEG: egKeysKuerzel[0] || null }) : undefined}
                      style={{ cursor: isClickable ? 'pointer' : 'default' }}
                      title={isClickable ? (egKeysKuerzel.length ? 'Klicken: Einzelanalyse (Regression, Trompetenkurve)' : '') : undefined}
                    >
                      <td className="compact-simple-label" title={k}>{k.length > 36 ? k.slice(0, 33) + '…' : k}</td>
                      <td className="compact-simple-value" style={{ textAlign: 'center' }}>{d.count}</td>
                      <td className="compact-simple-value" style={{ textAlign: 'center' }}>{d.n > 0 ? formatTime(d.min) : '–'}</td>
                      <td className="compact-simple-value" style={{ textAlign: 'center' }}>{d.n > 0 ? formatTime(d.max) : '–'}</td>
                      <td className="compact-simple-value" style={{ textAlign: 'center' }}>{d.n > 0 ? formatTime(d.mean) : '–'}</td>
                      <td className="compact-simple-value" style={{ textAlign: 'center' }}>{d.n > 0 ? formatTime(d.std) : '–'}</td>
                      <td className="compact-simple-value" style={{ textAlign: 'center' }}>{d.n > 0 ? formatTime(d.var) : '–'}</td>
                      <td className="compact-simple-value" style={{ textAlign: 'center' }}>{d.n > 0 ? formatTime(d.spanne) : '–'}</td>
                      <td className="compact-simple-value" style={{ textAlign: 'center' }}>{d.varKoeff != null ? d.varKoeff.toFixed(1) + ' %' : '–'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {statistikDisplay === 'bar' && (
          <div className="compact-statistik-bars" style={{ marginTop: 12 }}>
            {groupKeys.map(k => {
              const d = groupData[k];
              const pct = maxMean > 0 ? (d.mean / maxMean) * 100 : 0;
              return (
                <div key={k} className="compact-statistik-bar-row">
                  <div className="compact-statistik-bar-label" title={k}>{k.length > 40 ? k.slice(0, 37) + '…' : k} – Ø {d.n > 0 ? formatTime(d.mean) : '–'}</div>
                  <div className="compact-statistik-bar-track">
                    <div className="compact-statistik-bar-fill" style={{ width: pct + '%', minWidth: d.n > 0 ? 4 : 0 }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {statistikDisplay === 'kreis' && (() => {
          const slices = groupKeys.map(k => ({
            key: k,
            label: k.length > 40 ? k.slice(0, 37) + '...' : k,
            value: (groupData[k]?.totalTime || 0),
            count: groupData[k]?.count || 0,
            color: colorForKuerzel(k),
          }));
          const onSlice = (k) => {
            if (statistikGroupBy !== 'kuerzel') return;
            const d = groupData[k];
            const egKeys = [...new Set((d?.measurements || []).flatMap(m => (m.einflussgroessen && typeof m.einflussgroessen === 'object') ? Object.keys(m.einflussgroessen) : []))];
            setEaDrillDown({ key: k, kuerzel: k, egList: egKeys, selectedEG: egKeys[0] || null });
          };
          return (
            <div className="compact-bilanz-chart-wrap" style={{ marginTop: 12 }}>
              <AnalysisPieChart
                slices={slices}
                formatValue={formatTime}
                darkMode={darkMode}
                size={220}
                onSliceClick={statistikGroupBy === 'kuerzel' ? onSlice : undefined}
                ariaLabel="Statistik-Donut"
              />
            </div>
          );
        })()}

        {/* Einzelanalyse: Kürzel – nur bei Gruppierung "Kürzel", aus Klick auf Statistik-Zeile */}
        {eaDrillDown && statistikGroupBy === 'kuerzel' && (
          <div className="compact-statistik-ea-drilldown" style={{ marginTop: 12, padding: 12, border: '1px solid #e5e7eb', borderRadius: 8, background: darkMode ? '#111827' : '#fff' }}>
            <h4 className="compact-statistik-regression-title" style={{ margin: '0 0 8px 0', fontSize: 14 }}>Einzelanalyse: Kürzel „{eaDrillDown.kuerzel}“</h4>
            {(eaDrillDown.egList || []).length === 0 ? (
              <div className="compact-statistik-regression-line">Keine Einflussgrößen in dieser Zeile.</div>
            ) : (
              <>
                <label style={{ marginRight: 8 }}>Einflussgröße:</label>
                <select
                  value={eaDrillDown.selectedEG || ''}
                  onChange={e => setEaDrillDown(prev => prev ? { ...prev, selectedEG: e.target.value } : null)}
                  style={{ marginBottom: 12, padding: '4px 8px' }}
                >
                  {(() => {
                    const measurementsEA = Object.values(analysis.byKuerzel || {}).flatMap(gg => gg.measurements || []);
                    const { zeitbestimmend } = analyzeEinflussgroessenZeitbestimmend(measurementsEA);
                    return (eaDrillDown.egList || []).map(eg => (
                      <option key={eg} value={eg}>{eg}{zeitbestimmend.includes(eg) ? '' : ' (ohne Relevanz)'}</option>
                    ));
                  })()}
                </select>
                {eaDrillDown.selectedEG && (() => {
                  const eg = eaDrillDown.selectedEG;
                  const measurements = (analysis.byKuerzel[eaDrillDown.key]?.measurements || []).filter(m => m.einflussgroessen && (m.einflussgroessen[eg] != null && m.einflussgroessen[eg] !== ''));
                  const categories = [...new Set(measurements.map(m => String(m.einflussgroessen[eg])))].sort();
                  const points = measurements.map(m => ({ x: categories.indexOf(String(m.einflussgroessen[eg])) + 1, y: Number(m.vorgabeTime) || 0 }));
                  const pointMeta = measurements.map(m => ({ number: m.number, activity: m.isNote ? `📝 ${m.note || ''}` : (m.activity || '-') }));
                  const regression = categories.length >= 2 && points.length >= 3 ? simpleOLSWithBand(points.map(p => p.x), points.map(p => p.y)) : null;
                  return (
                    <EgRegressionChart
                      points={points}
                      pointMeta={pointMeta}
                      categories={categories}
                      regression={regression}
                      formatTime={formatTime}
                      darkMode={darkMode}
                      width={460}
                      height={260}
                    />
                  );
                })()}
              </>
            )}
          </div>
        )}

        {/* Einflussgrößen pro Kürzel+E / Kürzel+EA (nur Anzeige, keine Einzelanalyse – die gibt es nur bei Gruppierung Kürzel) */}
        {((statistikGroupBy === 'kuerzelE' && Object.keys(analysis.byKuerzelE || {}).some(k => k.includes(' | '))) || (statistikGroupBy === 'kuerzelEA' && Object.keys(analysis.byKuerzelEA || {}).some(k => k.includes(' | ')))) && (
          <div className="compact-statistik-regression">
            <h4 className="compact-statistik-regression-title">Einflussgrößen pro {statistikGroupBy === 'kuerzelEA' ? 'Kürzel+EA' : 'Kürzel+E'}</h4>
            <div className="compact-simple-table-wrapper" style={{ marginTop: 8, overflowX: 'auto' }}>
              <table className="compact-simple-table">
                <thead>
                  <tr>
                    <th>{statistikGroupBy === 'kuerzelEA' ? 'Kürzel + EA' : 'Kürzel + E'}</th>
                    <th>Verwendete Einflussgrößen</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(statistikGroupBy === 'kuerzelEA' ? (analysis.byKuerzelEA || {}) : (analysis.byKuerzelE || {})).sort((a, b) => a.localeCompare(b)).map(key => {
                    const g = (statistikGroupBy === 'kuerzelEA' ? analysis.byKuerzelEA : analysis.byKuerzelE)[key];
                    const first = (g.measurements || [])[0];
                    const egKeys = first && first.einflussgroessen && typeof first.einflussgroessen === 'object' ? Object.keys(first.einflussgroessen) : [];
                    return (
                      <tr key={key}>
                        <td className="compact-simple-label" title={key}>{key.length > 50 ? key.slice(0, 47) + '…' : key}</td>
                        <td className="compact-simple-value">{egKeys.join(', ') || '–'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Zeitbestimmend-Analyse, Multivariate Regression Sollzeit ~ EG, Clustering */}
        {(() => {
          const srcEA = statistikGroupBy === 'kuerzelEA' ? (analysis.byKuerzelEA || {}) : (statistikGroupBy === 'kuerzelE' ? (analysis.byKuerzelE || {}) : (analysis.byKuerzelEA || {}));
          const measurementsEA = Object.values(srcEA).flatMap(gg => gg.measurements || []);
          if (measurementsEA.length < 2) return null;
          const { allEG, zeitbestimmend, r2PerEG } = analyzeEinflussgroessenZeitbestimmend(measurementsEA);
          const { X, y, colNames } = buildDesignMatrixEA(measurementsEA, zeitbestimmend);
          const res = X.length >= X[0].length ? multilinearOLS(X, y) : { beta: colNames.map(() => 0), r2: 0 };
          const gData = statistikGroupBy === 'kuerzel' ? (analysis.byKuerzel || {}) : (statistikGroupBy === 'kuerzelE' ? (analysis.byKuerzelE || {}) : (analysis.byKuerzelEA || {}));
          const gKeys = Object.keys(gData);
          const points = gKeys.map(kk => { const g = gData[kk]; return [g.mean || 0, g.count || 0, parseFloat(g.avgGrade) || 0]; });
          let clusterLabels = [];
          if (points.length >= 3) {
            const kVal = Math.min(3, points.length);
            const assign = kMeans(points, kVal, 25);
            clusterLabels = gKeys.map((key, i) => ({ key, c: assign[i] }));
          }
          return (
            <div className="compact-statistik-regression">
              <h4 className="compact-statistik-regression-title">Zeitbestimmende Einflussgrößen (Sollzeit)</h4>
              {allEG.length === 0 ? (
                <div className="compact-statistik-regression-line">Keine Einflussgrößen in den Daten.</div>
              ) : (
                <>
                  <div className="compact-statistik-regression-line">
                    <strong>R² je EG (Sollzeit ~ Ausprägung):</strong>{' '}
                    {allEG.sort((a, b) => (r2PerEG[b] || 0) - (r2PerEG[a] || 0)).map(eg => `${eg}: ${(r2PerEG[eg] || 0).toFixed(3)}`).join('; ')}
                  </div>
                  <div className="compact-statistik-regression-line" style={{ marginBottom: 8 }}>
                    <strong>Einbezogen (R²≥1%):</strong> {zeitbestimmend.length ? zeitbestimmend.join(', ') : '–'}
                  </div>
                  {zeitbestimmend.length > 0 && res.beta && (
                    <div className="compact-statistik-regression-line" style={{ marginBottom: 12 }}>
                      <strong>Multivariate Regression Sollzeit ~ EG:</strong>{' '}
                      y = {res.beta.map((b, i) => i === 0 ? b.toFixed(1) : (b >= 0 ? '+ ' : '- ') + Math.abs(b).toFixed(2) + '·' + (colNames[i] || '?')).join(' ')} &emsp; R² = {res.r2.toFixed(3)}
                    </div>
                  )}
                </>
              )}
              {clusterLabels.length > 0 && (
                <>
                  <h4 className="compact-statistik-regression-title" style={{ marginTop: 16, marginBottom: 8 }}>Clustering (K-Means, k=3)</h4>
                  <div className="compact-statistik-clusters">
                    {[0, 1, 2].map(c => (
                      <div key={c} className="compact-statistik-cluster">
                        <strong>Cluster {c + 1}:</strong> {clusterLabels.filter(x => x.c === c).map(x => x.key).join(', ') || '–'}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          );
        })()}
      </>
    );
  };

  // Dashboard-Ansicht: 5 Kacheln untereinander (Portrait-konform)
  // Tile-Komponente: top-level (CompactDashboardTile), nicht hier neu definieren!
  const renderDashboardView = () => {
    // --- Bilanz Zeitarten (Kachel) ---
    const timeTypeKeys = Object.keys(analysis.byTimeType || {}).sort((a, b) => {
      if (a === 'Keine Zeitart') return 1;
      if (b === 'Keine Zeitart') return -1;
      return a.localeCompare(b);
    });
    const tileZeitartenRows = buildBilanzRows(analysis.byTimeType || {}, timeTypeKeys, colorForTimeType);
    const onTileTimeTypeDrill = (key) => {
      const data = (analysis.byTimeType || {})[key];
      if (data?.measurements?.length && setPivotDrillDown) {
        setCompactDrillOrigin('dashboard');
        setPivotDrillDown({ timeType: key, measurements: data.measurements });
      }
    };

    // --- Bilanz Kuerzel (Kachel) ---
    const kuerzelKeys = Object.keys(analysis.byKuerzel || {}).sort((a, b) => {
      if (a === 'Kein Kürzel') return 1;
      if (b === 'Kein Kürzel') return -1;
      return a.localeCompare(b);
    });
    const tileKuerzelRows = buildBilanzRows(analysis.byKuerzel || {}, kuerzelKeys, colorForKuerzel);
    const onTileKuerzelDrill = (key) => {
      const data = (analysis.byKuerzel || {})[key];
      if (data?.measurements?.length && setPivotDrillDown) {
        setCompactDrillOrigin('dashboard');
        setPivotDrillDown({ kuerzel: key, measurements: data.measurements });
      }
    };

    // --- Zeitstrahl (Kachel) ---
    const flatMeasurements = Object.values(analysis.byTimeType || {}).flatMap(d => d.measurements || []);
    const byWaNr = {};
    flatMeasurements.forEach(m => {
      const key = m.resumeNumber ?? '—';
      if (!byWaNr[key]) byWaNr[key] = [];
      byWaNr[key].push(m);
    });
    const waOrderKeys = Object.keys(byWaNr).sort((a, b) => {
      if (a === '—') return 1;
      if (b === '—') return -1;
      const na = Number(a);
      const nb = Number(b);
      if (!isNaN(na) && !isNaN(nb)) return na - nb;
      return String(a).localeCompare(String(b));
    });
    const tileWaKeys = waOrderKeys.slice(0, 6);
    const getColorM = (m) => colorForTimeType((m.timeType == null || m.timeType === '') ? 'Keine Zeitart' : m.timeType);
    const toT = (m) => {
      const v = m?.timestamp ? new Date(m.timestamp).getTime() : 0;
      return (typeof v === 'number' && !isNaN(v)) ? v : 0;
    };

    // --- Pivot (Kachel: kompakte Tabelle, gleiche Render-Logik wie Vollansicht) ---
    // Wir benutzen die existierende renderPivotView, aber in einem scroll-baren Container.

    // --- Statistik (Kachel: Top-Liste der Kuerzel nach Mittelwert) ---
    const statSrc = analysis.byKuerzel || {};
    const statKeys = Object.keys(statSrc).sort((a, b) => (statSrc[b]?.mean || 0) - (statSrc[a]?.mean || 0)).slice(0, 6);

    return (
      <div className="compact-analysis-dashboard">
        <CompactDashboardTile
          title="Bilanz Zeitarten"
          hint="Verteilung der gemessenen IST- und SOLL-Zeiten ueber die Zeitarten"
          openMode="timeType"
          onOpen={switchTab}
          extra={(
            <TimePill
              value={bilanzZeitartenTimeMode}
              onChange={setBilanzZeitartenTimeMode}
              darkMode={darkMode}
              options={[
                { id: 'ist', label: 'IST' },
                { id: 'soll', label: 'SOLL' },
                { id: 'vergleich', label: 'Vgl.' },
              ]}
              ariaLabel="Zeit-Modus Zeitarten"
            />
          )}
        >
          {tileZeitartenRows.length === 0 ? (
            <div className="compact-dashboard-empty">Keine Daten.</div>
          ) : (
            renderBilanzPies(tileZeitartenRows, bilanzZeitartenTimeMode, onTileTimeTypeDrill, 180)
          )}
        </CompactDashboardTile>

        <CompactDashboardTile
          title="Bilanz Kürzel"
          hint="Verteilung der Zeiten je Kuerzel"
          openMode="kuerzel"
          onOpen={switchTab}
          extra={(
            <TimePill
              value={bilanzKuerzelTimeMode}
              onChange={setBilanzKuerzelTimeMode}
              darkMode={darkMode}
              options={[
                { id: 'ist', label: 'IST' },
                { id: 'soll', label: 'SOLL' },
                { id: 'vergleich', label: 'Vgl.' },
              ]}
              ariaLabel="Zeit-Modus Kuerzel"
            />
          )}
        >
          {tileKuerzelRows.length === 0 ? (
            <div className="compact-dashboard-empty">Keine Daten.</div>
          ) : (
            renderBilanzBars(tileKuerzelRows, bilanzKuerzelTimeMode, onTileKuerzelDrill)
          )}
        </CompactDashboardTile>

        <CompactDashboardTile title="Zeitstrahl" hint="Erste WA-Nr aufgeklappt; vollstaendiger Zeitstrahl ueber 'Oeffnen'" openMode="zeitstrahl" onOpen={switchTab}>
          {tileWaKeys.length === 0 ? (
            <div className="compact-dashboard-empty">Keine Daten.</div>
          ) : (
            <div className="compact-zeitstrahl compact-dashboard-zeitstrahl">
              {tileWaKeys.map(waKey => {
                const list = [...(byWaNr[waKey] || [])].sort((a, b) => toT(a) - toT(b));
                return (
                  <ZeitstrahlWaBlock
                    key={waKey}
                    waKey={waKey}
                    list={list}
                    expanded={false}
                    onToggle={() => switchTab('zeitstrahl')}
                    getColor={getColorM}
                    formatTime={formatTime}
                    onSegmentEnter={() => {}}
                    onSegmentLeave={() => {}}
                    compact
                  />
                );
              })}
              {waOrderKeys.length > tileWaKeys.length && (
                <div className="compact-dashboard-more">+{waOrderKeys.length - tileWaKeys.length} weitere – Öffnen →</div>
              )}
            </div>
          )}
        </CompactDashboardTile>

        <CompactDashboardTile title="Pivot" hint="Kreuztabelle Aktivitaet x Zeitart" openMode="pivot" onOpen={switchTab}>
          <div className="compact-dashboard-pivot-wrap">
            {renderPivotView()}
          </div>
        </CompactDashboardTile>

        <CompactDashboardTile title="Statistik" hint="Top-Kuerzel nach Mittelwert" openMode="statistik" onOpen={switchTab}>
          {statKeys.length === 0 ? (
            <div className="compact-dashboard-empty">Keine Daten.</div>
          ) : (
            <ul className="compact-dashboard-stat-list">
              {statKeys.map(k => {
                const d = statSrc[k] || {};
                return (
                  <li key={k} className="compact-dashboard-stat-row">
                    <span className="compact-dashboard-stat-color" style={{ backgroundColor: colorForKuerzel(k) }} />
                    <span className="compact-dashboard-stat-label" title={k}>{k.length > 36 ? k.slice(0, 33) + '…' : k}</span>
                    <span className="compact-dashboard-stat-value">Ø {d.n > 0 ? formatTime(d.mean) : '–'}</span>
                    <span className="compact-dashboard-stat-count">n={d.count || 0}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </CompactDashboardTile>
      </div>
    );
  };

  return (
    <div className={`compact-analysis-dialog ${darkMode ? 'dark-mode' : ''}`}>
      <div className="compact-analysis-header">
        <h3>{t('common.auswertung_with_icon')}</h3>
        {!pivotDrillDown && !eaDrillDown && (
          <div className="compact-analysis-view-mode">
            <button
              className={`compact-view-mode-btn ${viewMode === 'dashboard' ? 'active' : ''}`}
              onClick={() => switchTab('dashboard')}
              title="Dashboard: Übersicht aller Auswertungen als Kacheln"
            >
              📊 Dashboard
            </button>
            <button
              className={`compact-view-mode-btn ${viewMode === 'timeType' ? 'active' : ''}`}
              onClick={() => switchTab('timeType')}
              title={t('compact_analysis.time_type_view_title')}
            >
              {t('compact_analysis.time_type_view_btn')}
            </button>
            <button
              className={`compact-view-mode-btn ${viewMode === 'kuerzel' ? 'active' : ''}`}
              onClick={() => switchTab('kuerzel')}
              title="Nach Kürzeln"
            >
              Kürzel
            </button>
            <button
              className={`compact-view-mode-btn ${viewMode === 'zeitstrahl' ? 'active' : ''}`}
              onClick={() => switchTab('zeitstrahl')}
              title={t('compact_analysis.zeitstrahl_button_title')}
            >
              {t('compact_analysis.zeitstrahl_title')}
            </button>
            <button
              className={`compact-view-mode-btn ${viewMode === 'pivot' ? 'active' : ''}`}
              onClick={() => switchTab('pivot')}
              title={t('compact_analysis.pivot_button_title')}
            >
              Pivot
            </button>
            <button
              className={`compact-view-mode-btn ${viewMode === 'statistik' ? 'active' : ''}`}
              onClick={() => switchTab('statistik')}
              title={t('compact_analysis.statistik_button_title')}
            >
              Statistik
            </button>
          </div>
        )}
        {pivotDrillDown && (
          <button 
            className="btn-close-drilldown" 
            onClick={closeDrillDown}
            title={t('compact_analysis.drilldown_close_title')}
          >
            ✕ {t('compact_analysis.zurueck')}
          </button>
        )}
        {!pivotDrillDown && eaDrillDown && (
          <button 
            className="btn-close-drilldown" 
            onClick={closeDrillDown}
            title={t('compact_analysis.einzelanalyse_close_title')}
          >
            ← {t('compact_analysis.zurueck')}
          </button>
        )}
        <button 
          className="btn-close-analysis" 
          onClick={onClose}
          title={t('common.auswertung_close')}
        >
          ✕
        </button>
      </div>
      
      {!pivotDrillDown ? (
        <div className="compact-analysis-content">
          {viewMode === 'dashboard' && renderDashboardView()}
          {viewMode === 'timeType' && renderTimeTypeView()}
          {viewMode === 'kuerzel' && renderKuerzelView()}
          {viewMode === 'zeitstrahl' && renderZeitstrahlView()}
          {viewMode === 'pivot' && renderPivotView()}
          {viewMode === 'statistik' && renderStatistikView()}

          {viewMode !== 'dashboard' && (
          <div className="compact-pivot-legend">
            <div className="compact-pivot-legend-item">
              <span className="compact-pivot-legend-label">{t('compact_analysis.legend_anzahl')}</span>
              <span className="compact-pivot-legend-desc">{t('compact_analysis.legend_anzahl_desc')}</span>
            </div>
            <div className="compact-pivot-legend-item">
              <span className="compact-pivot-legend-label">{t('compact_analysis.legend_gesamtzeit')}</span>
              <span className="compact-pivot-legend-desc">{t('compact_analysis.legend_gesamtzeit_desc')}</span>
            </div>
            <div className="compact-pivot-legend-item">
              <span className="compact-pivot-legend-label">{t('compact_analysis.legend_leistungsgrad')}</span>
              <span className="compact-pivot-legend-desc">{t('compact_analysis.legend_leistungsgrad_desc')}</span>
            </div>
            {viewMode === 'pivot' && (
              <div className="compact-pivot-legend-hint">
                {t('compact_analysis.tip_cell')}
              </div>
            )}
            {(viewMode === 'kuerzel' || viewMode === 'timeType') && (
              <div className="compact-pivot-legend-hint">
                {t('compact_analysis.tip_row')}
              </div>
            )}
            {viewMode === 'zeitstrahl' && (
              <div className="compact-pivot-legend-hint">
                {t('compact_analysis.tip_zeitstrahl_click')}
              </div>
            )}
            {viewMode === 'zeitstrahl' && (
              <div className="compact-pivot-legend-hint">
                {t('compact_analysis.tip_zeitstrahl_segmente')}
              </div>
            )}
            {viewMode === 'statistik' && (
              <div className="compact-pivot-legend-hint">
                {t('compact_analysis.tip_statistik')}
              </div>
            )}
          </div>
          )}
        </div>
      ) : (
        <div className="compact-pivot-drilldown">
          <div className="compact-drilldown-header">
            <h4>
              {pivotDrillDown.activity && pivotDrillDown.timeType && `${pivotDrillDown.activity} × ${pivotDrillDown.timeType}`}
              {pivotDrillDown.kuerzel && `Kürzel: ${pivotDrillDown.kuerzel}`}
              {pivotDrillDown.timeType && !pivotDrillDown.activity && `${t('compact_analysis.drilldown_zeitart')} ${pivotDrillDown.timeType}`}
            </h4>
            <div className="compact-drilldown-summary">
              <span>{pivotDrillDown.measurements.length === 1 ? t('compact_analysis.messpunkt_count_one', { count: pivotDrillDown.measurements.length }) : t('compact_analysis.messpunkt_count_other', { count: pivotDrillDown.measurements.length })}</span>
              <span>{t('compact_analysis.drilldown_gesamtzeit')} {formatTime(pivotDrillDown.measurements.reduce((sum, m) => sum + m.individualTime, 0))}</span>
            </div>
          </div>
          <div className="compact-drilldown-measurements">
            {pivotDrillDown.measurements.map((m, idx) => (
              <div key={idx} className="compact-drilldown-item">
                <div className="compact-drilldown-item-header">
                  <span className="compact-drilldown-number">#{m.number}</span>
                  <span className="compact-drilldown-grade">{t('compact_analysis.drilldown_leistungsgrad')} {m.grade}%</span>
                  <span className="compact-drilldown-time">{formatTime(m.individualTime)}</span>
                </div>
                <div className="compact-drilldown-item-details">
                  {m.activity && (
                    <span>{t('compact_analysis.drilldown_taetigkeit')} {m.activity}</span>
                  )}
                  {m.timestamp && (
                    <span>{t('compact_analysis.drilldown_zeitpunkt')} {new Date(m.timestamp).toLocaleString('de-DE')}</span>
                  )}
                  {m.note && (
                    <span className="compact-drilldown-note">{t('compact_analysis.drilldown_notiz')} {m.note}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CompactAnalysisDialog;
